/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CargomaticModal from 'components/v2/modal';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import formatMoney from 'utils/formatters/Money';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import SimpleCard from 'components/v2/SimpleCard';
import Switch from 'react-switch';
import CargomaticInput from 'components/v2/input';
import { showToast } from 'stores/ToastStore';
import Select from 'components/v2/select';
import { fetchCreditMemoReasonCodes } from 'utils/credit-memo';
import Spinner from 'components/ui/Spinner';
function getReceivablesToRebill(shipment, selectedLineItem) {
    return shipment.receivables.filter((receivable) => receivable.invoice_id === selectedLineItem.invoice._id &&
        receivable.type !== 'shipper_cost');
}
export default function CreditMemoModal({ showModal, shipment, selectedLineItem, disableSave, onSave, onClose, }) {
    const { t } = useTranslation();
    const [receivables, setReceivables] = useState([]);
    const [rebillRequired, setRebillRequired] = useState(true);
    const [adjustedItemsDetails, setAdjustedItemsDetails] = useState([]);
    const [errors, setErrors] = useState([]);
    const [creditTotal, setCreditTotal] = useState(0);
    const [reasonCodes, setReasonCodes] = useState([]);
    const fetchReasonCodes = async () => {
        try {
            const result = await fetchCreditMemoReasonCodes();
            if (result && result.length) {
                setReasonCodes(result);
            }
            else {
                showToast(t('NOT_FOUND_REASON_CODES'), '', 'error');
            }
        }
        catch (e) {
            showToast(t('FAILED_FETCH_REASON_CODES'), '', 'error');
        }
    };
    function getInitalValues() {
        const receivablesToRebill = getReceivablesToRebill(shipment, selectedLineItem);
        setReceivables(receivablesToRebill);
        setAdjustedItemsDetails(shipment.receivables
            .filter((item) => item.invoice_id === selectedLineItem.invoice._id)
            .map((item) => {
            let amount = 0;
            if (item.type === 'shipper_cost')
                amount = item.value || 0;
            else
                amount = item.unit_value || item.value || 0;
            let message = (item === null || item === void 0 ? void 0 : item.charge_code) === 'FSC'
                ? 'Fuel Surcharge adjustment'
                : item === null || item === void 0 ? void 0 : item.message;
            return Object.assign(Object.assign({}, item), { reasonCode: '', notes: '', amount, units: item.unit_qty || 1, billing_type: 'unchanged', total_amount: amount * (item.unit_qty || 1), message });
        }));
        setErrors(shipment.receivables
            .filter((item) => item.invoice_id === selectedLineItem.invoice._id)
            .map((item) => {
            return {
                reasonCode: null,
                notes: null,
                amount: null,
                type: item.type,
                _id: item._id,
                units: null,
            };
        }));
        setCreditTotal(shipment.receivables
            .filter((item) => item.invoice_id === selectedLineItem.invoice._id)
            .reduce((acc, curr) => {
            acc += curr.value;
            return acc;
        }, 0));
    }
    useEffect(() => {
        getInitalValues();
    }, [showModal]);
    useEffect(() => {
        fetchReasonCodes();
    }, []);
    const getError = (type, _id) => {
        const errorItem = errors.find((item) => item._id === _id);
        if (errorItem)
            return errorItem[type];
    };
    function handleError(type, _id, error) {
        setErrors((prev) => {
            let previous = [...prev];
            const index = previous.findIndex((item) => item._id === _id);
            previous[index][type] = error;
            return previous;
        });
    }
    function runValidationChecks() {
        return new Promise((resolve) => {
            adjustedItemsDetails.forEach((item) => {
                if (item.amount < 0) {
                    handleError('amount', item._id, t('CREDIT_MEMO_AMOUNT_ERROR'));
                }
                else {
                    handleError('amount', item._id, null);
                }
                if (item.notes.trim().length <= 0) {
                    handleError('notes', item._id, t('EMPTY_NOTES_ERROR'));
                }
                else if (item.notes.trim().length > 100) {
                    handleError('notes', item._id, t('CREDIT_MEMO_NOTES_CHARACTER_LENGTH_ERROR'));
                }
                else {
                    handleError('notes', item._id, null);
                }
                if (!item.reasonCode) {
                    handleError('reasonCode', item._id, t('REQUIRED_REASON_CODE'));
                }
                else {
                    handleError('reasonCode', item._id, null);
                }
                if (!item.units || item.units < 0) {
                    handleError('units', item._id, t('CREDIT_MEMO_UNITS_ERROR'));
                }
                else {
                    handleError('units', item._id, null);
                }
            });
            resolve();
        });
    }
    async function handleSave() {
        await runValidationChecks();
        let errorsValidated = true;
        let errorsToCheck = errors;
        errorsToCheck.forEach((item) => {
            if (item.reasonCode || item.amount || item.notes || item.units)
                errorsValidated = false;
        });
        if (!errorsValidated) {
            return;
        }
        const data = {
            rebill: rebillRequired,
            adjustedItems: adjustedItemsDetails,
        };
        onSave(data);
    }
    const onChange = (type, _id, value) => {
        setAdjustedItemsDetails((previous) => {
            let prev = [...previous];
            const index = prev.findIndex((item) => item._id.toString() === _id.toString());
            const receivable = shipment.receivables.find((item) => item._id === _id);
            if (type === 'billing_type') {
                let units = 0;
                if (value === 'changed') {
                    units = prev[index].units;
                }
                if (value === 'unchanged') {
                    units = receivable.unit_qty;
                    prev[index].amount = shipment.receivables.find((el) => el._id === _id).unit_value;
                }
                prev[index].units = units;
                prev[index].total_amount = prev[index].amount * units;
            }
            prev[index][type] = value;
            if (prev[index].amount === receivable.unit_value &&
                prev[index].units === receivable.unit_qty) {
                prev[index].billing_type = 'unchanged';
            }
            else {
                prev[index].billing_type = 'changed';
            }
            prev[index].total_amount = prev[index].amount * prev[index].units;
            return prev;
        });
    };
    const getValue = (type, _id) => {
        const ledgerItem = adjustedItemsDetails.find((item) => item._id === _id);
        if (ledgerItem)
            return ledgerItem[type];
    };
    const renderLineItems = (index, lineItem) => {
        const disableAll = lineItem.type === 'shipper_cost';
        return ((React.createElement("tr", { "data-testid": `tr-83de3d88-${index}` },
            React.createElement("td", { "data-testid": "td-06cb9317" }, lineItem.message),
            React.createElement("td", { "data-testid": "td-e2c59226" }, reasonCodes.length > 0 ? (React.createElement(Select, { dataTestId: "Select-7d9cb490", selected: getValue('reasonCode', lineItem._id), onSelect: ({ description: value }) => onChange('reasonCode', lineItem._id, value), items: reasonCodes, valueKey: "description", textKey: "description", caption: t('REASON_CODE'), className: "mt-2" })) : (React.createElement(Spinner, { dataTestId: "Spinner-bfb606d1", showMessage: false }))),
            React.createElement("td", { "data-testid": "td-3d6017b5" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-8fdf2f92", type: "text", value: getValue('notes', lineItem._id), placeholder: t('ENTER_NOTE_PLACEHOLDER'), onChange: (value) => onChange('notes', lineItem._id, value), disabled: disableAll }),
                React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-ff54de58", className: "mt-1" }, getError('notes', lineItem._id) || '')),
            rebillRequired && (React.createElement(React.Fragment, null,
                React.createElement("td", { "data-testid": "td-58315d99" },
                    React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-93eb265e", type: "number", value: getValue('amount', lineItem._id), placeholder: t('DOLLAR_RATE_PER_UNIT'), onChange: (value) => onChange('amount', lineItem._id, parseFloat(value)), disabled: disableAll }),
                    React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-1ac6410c", className: "mt-1" }, getError('amount', lineItem._id) || '')),
                React.createElement("td", { "data-testid": "td-1ef39ed9" },
                    React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-794e1a79", type: "number", placeholder: t('UNIT', { count: 5 }), value: getValue('units', lineItem._id), onChange: (value) => onChange('units', lineItem._id, parseFloat(value)), disabled: disableAll || !lineItem.accessorial_request_id }),
                    React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-6aba47c2", className: "mt-1" }, getError('units', lineItem._id) || '')),
                React.createElement("td", { "data-testid": "td-aabe9ce8" }, (lineItem.accessorial_request_id ||
                    lineItem.charge_code === 'FSC') &&
                    formatMoney(getValue('total_amount', lineItem._id))))))));
    };
    const renderShipperCostItems = (shipper_cost_lineItem) => {
        if (!shipper_cost_lineItem)
            return null;
        const shipper_cost_id = shipper_cost_lineItem._id;
        if (!shipper_cost_id || !shipper_cost_id.length)
            return null;
        return ((React.createElement("tr", { "data-testid": "tr-ce798cbf" },
            React.createElement("td", { "data-testid": "td-1e956649" }, shipper_cost_lineItem.message),
            React.createElement("td", { "data-testid": "td-55fae75d" }, reasonCodes.length > 0 ? (React.createElement(Select, { dataTestId: "Select-a1177f87", value: getValue('reasonCode', shipper_cost_id), onSelect: ({ description: value }) => onChange('reasonCode', shipper_cost_id, value), items: reasonCodes, valueKey: "description", textKey: "description", caption: t('REASON_CODE'), className: "mt-2" })) : (React.createElement(Spinner, { dataTestId: "Spinner-5d1534e5", showMessage: false }))),
            React.createElement("td", { "data-testid": "td-e387bed2" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-e74789cd", type: "text", value: getValue('notes', shipper_cost_id), placeholder: t('ENTER_NOTE_PLACEHOLDER'), onChange: (value) => {
                        onChange('notes', shipper_cost_id, value);
                    } }),
                React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-00d6ea19", className: "mt-1" }, getError('notes', shipper_cost_id) || '')),
            rebillRequired && (React.createElement(React.Fragment, null,
                React.createElement("td", { "data-testid": "td-4b9958eb" },
                    React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-6a401338", type: "number", value: getValue('amount', shipper_cost_id), placeholder: t('NEW_DOLLAR_VALUE_PLACEHOLDER'), onChange: (value) => onChange('amount', shipper_cost_id, parseFloat(value)) }),
                    React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-c34b0981", className: "mt-1" }, getError('amount', shipper_cost_id) || '')),
                React.createElement("td", { "data-testid": "td-2814ebf8" }),
                React.createElement("td", { "data-testid": "td-2363244f" }, formatMoney(getValue('amount', shipper_cost_id))))))));
    };
    const renderRebillTotal = () => {
        const rebillTotal = adjustedItemsDetails.reduce((total, item) => {
            return total + item.total_amount;
        }, 0);
        return ((React.createElement("tr", { "data-testid": "tr-79fc9c21" },
            React.createElement("td", { "data-testid": "td-cdd64eff", colSpan: 5 },
                React.createElement("span", { "data-testid": "span-3cc0ac3f", className: "font-medium" }, t('REBILL_TOTAL'))),
            React.createElement("td", { "data-testid": "td-b2a4e223" },
                React.createElement("span", { "data-testid": "span-20e59865" }, formatMoney(rebillTotal))))));
    };
    const renderHeaders = () => {
        return ((React.createElement("tr", { "data-testid": "tr-e9d6d7b4" },
            React.createElement("th", { "data-testid": "th-6d1c0351" }, t('CHARGE')),
            React.createElement("th", { "data-testid": "th-737443e3" }, t('REASON_CODE')),
            React.createElement("th", { "data-testid": "th-9198c888" }, titleize(t('NOTE', { count: 4 }))),
            receivables.length > 0 && rebillRequired ? (React.createElement(React.Fragment, null,
                React.createElement("th", { "data-testid": "th-0abc2ccb" }, t('RATE_IN_DOLLAR')),
                React.createElement("th", { "data-testid": "th-111240ac" }, t('UNIT', { count: 5 })),
                React.createElement("th", { "data-testid": "th-a7ae57cb" }, t('TOTAL')))) : (React.createElement(React.Fragment, null,
                React.createElement("th", { "data-testid": "th-580ea671" }),
                " ",
                React.createElement("th", { "data-testid": "th-2ca152d8" }),
                " ",
                React.createElement("th", { "data-testid": "th-60c945c6" }))))));
    };
    const renderConfigDetails = () => {
        var _a;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { "data-testid": "div-5e1d07ac", className: "grid grid-cols-2 gap-5 max-w-md " },
                React.createElement(SimpleCard, { dataTestId: "SimpleCard-ece23dbc", title: t('CREDIT_INVOICE'), key: "invoice number" },
                    React.createElement("p", { "data-testid": "p-be096079", className: " font-medium mt-1 text-gray-900 tracking-tight text-base" }, (_a = selectedLineItem === null || selectedLineItem === void 0 ? void 0 : selectedLineItem.invoice) === null || _a === void 0 ? void 0 : _a.number)),
                React.createElement(SimpleCard, { dataTestId: "SimpleCard-3b6ecfd1", title: t('CREDIT_MEMO_TOTAL'), key: "credit_memo_total" },
                    React.createElement("p", { "data-testid": "p-59948064", className: " font-medium mt-1 text-gray-900 tracking-tight text-base" }, formatMoney(creditTotal)))),
            React.createElement("div", { "data-testid": "div-e2c0a376", className: "flex items-center my-2 mt-4 mb-2" },
                React.createElement(Switch, { checked: rebillRequired, onChange: (value) => {
                        setRebillRequired(value);
                    }, height: 20, width: 48, onColor: "#08f" }),
                React.createElement("label", { "data-testid": "label-d29b8ef9", className: "w-8 ml-2" }, t('REBILL')))));
    };
    const renderTableBody = () => {
        const shipperCostLineItems = shipment.receivables.filter((item) => item.invoice_id === selectedLineItem.invoice._id &&
            item.type === 'shipper_cost');
        return (React.createElement(React.Fragment, null,
            shipperCostLineItems.map((item) => {
                return renderShipperCostItems(item);
            }),
            receivables.map((receivable, index) => {
                return renderLineItems(index, receivable);
            })));
    };
    const renderModalBody = () => {
        return ((React.createElement("div", { "data-testid": "div-704929e1" },
            renderConfigDetails(),
            React.createElement("div", { "data-testid": "div-df268eb3", className: " overflow-scroll table-wrapper" },
                React.createElement("table", { "data-testid": "table-27ac4c23", className: "default-table " },
                    React.createElement("thead", { "data-testid": "thead-8b60e610" }, renderHeaders()),
                    React.createElement("tbody", { "data-testid": "tbody-0baa107f" }, renderTableBody()),
                    React.createElement("tfoot", { "data-testid": "tfoot-f8cce3fb" }, rebillRequired && renderRebillTotal()))),
            React.createElement("b", { "data-testid": "b-66869654" }))));
    };
    const renderFooter = () => {
        return ((React.createElement("div", { "data-testid": "div-f2d78ad4", className: "flex flex-col-reverse md:flex-row md:justify-end gap-2" },
            React.createElement("button", { "data-testid": "button-a465a961", className: " button secondary-action", onClick: onClose }, t('CANCEL')),
            React.createElement("button", { "data-testid": "button-f4e3538e", className: "button button-green ", onClick: () => handleSave(), disabled: disableSave }, titleize(t('SAVE'))))));
    };
    return ((React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-6071e77a", dialogClassName: "modal-override modal-dialog", title: t('CREATE_CREDIT_MEMO'), style: { top: '10%' }, show: showModal, onHide: onClose, body: renderModalBody(), footer: renderFooter(), location: "middle", className: "md:w-4/5 w-full" })));
}
CreditMemoModal.propTypes = {
    showModal: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    shipment: PropTypes.object.isRequired,
    selectedLineItem: PropTypes.object.isRequired,
};
